import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import KPICard from "./KPICard";
import { COLOR_PALETTE } from "./ChartBuilder";

const CHART_FONT = "'Segoe UI Regular', 'Work Sans', sans-serif";

const ChartPreview = ({ chartType, previewData, loadingPreview, title }) => {
  const chartData = previewData
    ? (previewData.labels || []).map((label, i) => ({
      name: label,
      value: (previewData.values || [])[i] ?? 0,
    }))
    : null;

  const previewColors = (previewData?.labels || []).map(
    (_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]
  );

  const renderPreviewChart = () => {
    if (!chartData) return null;
    if (chartType === "bar") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(150 10% 46%)", fontFamily: CHART_FONT }} axisLine={{ stroke: "hsl(140 8% 88%)" }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(150 10% 46%)", fontFamily: CHART_FONT }} axisLine={false} tickLine={false} />
            <Tooltip />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
              {chartData.map((_, i) => <Cell key={i} fill={previewColors[i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(150 10% 46%)", fontFamily: CHART_FONT }} axisLine={{ stroke: "hsl(140 8% 88%)" }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(150 10% 46%)", fontFamily: CHART_FONT }} axisLine={false} tickLine={false} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke={COLOR_PALETTE[0]} strokeWidth={2.5} dot={{ fill: COLOR_PALETTE[0], strokeWidth: 2, r: 3, stroke: "#fff" }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === "pie" || chartType === "doughnut") {
      const innerRadius = chartType === "doughnut" ? "55%" : 0;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={innerRadius} outerRadius="80%" paddingAngle={2} stroke="none">
              {chartData.map((_, i) => <Cell key={i} fill={previewColors[i]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <Bar dataKey="value" fill={COLOR_PALETTE[0]} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 1 }}>
        Preview
      </Typography>
      <Box sx={{ height: 250, p: 2, borderRadius: "calc(-2px + 0.75rem)", border: "1px solid hsl(140 8% 88%)", backgroundColor: "hsl(140 10% 98%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {loadingPreview ? (
          <CircularProgress size={32} sx={{ color: "hsl(150 38% 17%)" }} />
        ) : previewData ? (
          chartType === "kpi" ? (
            <Box sx={{ width: "100%", maxWidth: 320 }}>
              <KPICard
                value={previewData.value}
                previousValue={previewData.previous_value}
                delta={previewData.delta}
                title={title || "Preview"}
              />
            </Box>
          ) : chartData ? (
            <Box sx={{ width: "100%", height: "100%" }}>
              {renderPreviewChart()}
            </Box>
          ) : null
        ) : (
          <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>
            Select a field to preview
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChartPreview;
